// FAR Lifecycle Phase Tracker Component
// Displays procurement phases with FAR references and allows phase changes

import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  FileText,
  AlertCircle,
  FileSearch,
  ScrollText,
  Zap,
  Check,
  ChevronRight,
  Info,
  ArrowUp,
  ArrowDown,
  Loader2,
  BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FAR Lifecycle Phases with references and guidance
const PROCUREMENT_PHASES = [
  {
    id: 'FORECAST',
    label: 'Forecast',
    shortLabel: 'Forecast',
    icon: Search,
    farReference: 'FAR 5.101',
    description: 'Anticipated procurement published in agency forecast. Early market research stage.',
    guidance: 'Focus on: Capability Match, Past Performance alignment, early teaming discussions.',
    color: 'slate',
    bgColor: 'bg-slate-100',
    activeColor: 'bg-slate-600',
    borderColor: 'border-slate-300'
  },
  {
    id: 'SPECIAL_NOTICE',
    label: 'Special Notice',
    shortLabel: 'Special',
    icon: AlertCircle,
    farReference: 'FAR 5.205',
    description: 'Agency notice for market research, not a solicitation. May request capability statements.',
    guidance: 'Focus on: Capability Statement submission, initial competitive landscape analysis.',
    color: 'blue',
    bgColor: 'bg-blue-100',
    activeColor: 'bg-blue-600',
    borderColor: 'border-blue-300'
  },
  {
    id: 'SOURCES_SOUGHT',
    label: 'Sources Sought (RFI)',
    shortLabel: 'RFI',
    icon: FileSearch,
    farReference: 'FAR 5.204 / 15.201',
    description: 'Request for Information to identify potential sources. Response helps shape requirements.',
    guidance: 'Focus on: Capability Match analysis, identifying potential teaming partners, RFI response drafting.',
    color: 'purple',
    bgColor: 'bg-purple-100',
    activeColor: 'bg-purple-600',
    borderColor: 'border-purple-300'
  },
  {
    id: 'PRE_SOLICITATION',
    label: 'Pre-Solicitation',
    shortLabel: 'Pre-Sol',
    icon: FileText,
    farReference: 'FAR 5.204',
    description: 'Notice published before formal RFP release. Final preparation window.',
    guidance: 'Focus on: Team finalization, compliance matrix prep, Section L/M research.',
    color: 'amber',
    bgColor: 'bg-amber-100',
    activeColor: 'bg-amber-600',
    borderColor: 'border-amber-300'
  },
  {
    id: 'SOLICITATION',
    label: 'Solicitation (RFP)',
    shortLabel: 'RFP',
    icon: ScrollText,
    farReference: 'FAR 15.203',
    description: 'Formal Request for Proposal released. Proposal development and submission phase.',
    guidance: 'Focus on: Section L/M compliance, proposal writing, volume coordination, red team reviews.',
    color: 'teal',
    bgColor: 'bg-teal-100',
    activeColor: 'bg-teal-600',
    borderColor: 'border-teal-300'
  },
  {
    id: 'COMBINED_SYNOPSIS',
    label: 'Combined Synopsis/Solicitation',
    shortLabel: 'Combined',
    icon: Zap,
    farReference: 'FAR 12.603 / 5.207',
    description: 'Simplified acquisition combining synopsis and solicitation. Compressed timeline - act fast!',
    guidance: 'URGENT: Skip RFI phase, move directly to proposal drafting. Section L/M compliance is critical.',
    color: 'red',
    bgColor: 'bg-red-100',
    activeColor: 'bg-red-600',
    borderColor: 'border-red-300',
    isAccelerated: true
  }
];

export function PhaseTracker({ 
  pursuitId, 
  currentPhase, 
  authToken, 
  onPhaseChange,
  canEdit = true,
  compact = false 
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [targetPhase, setTargetPhase] = useState(null);
  
  const currentPhaseIndex = PROCUREMENT_PHASES.findIndex(p => p.id === currentPhase);
  const effectivePhase = currentPhase || 'FORECAST';
  
  const handlePhaseClick = (phase, index) => {
    if (!canEdit || isUpdating) return;
    if (phase.id === currentPhase) return;
    
    setTargetPhase({ phase, index, isPromotion: index > currentPhaseIndex });
    setShowConfirmDialog(true);
  };
  
  const confirmPhaseChange = async () => {
    if (!targetPhase) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ procurement_phase: targetPhase.phase.id })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update phase');
      }
      
      toast.success(
        targetPhase.isPromotion 
          ? `Promoted to ${targetPhase.phase.label}` 
          : `Moved back to ${targetPhase.phase.label}`
      );
      
      if (onPhaseChange) {
        onPhaseChange(targetPhase.phase.id);
      }
      
      setShowConfirmDialog(false);
      setTargetPhase(null);
    } catch (err) {
      console.error('Error updating phase:', err);
      toast.error('Failed to update procurement phase');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // For Combined Synopsis, show it jumps to the end
  const isCombinedSynopsis = effectivePhase === 'COMBINED_SYNOPSIS';
  
  if (compact) {
    // Compact view for pipeline cards
    const phase = PROCUREMENT_PHASES.find(p => p.id === effectivePhase) || PROCUREMENT_PHASES[0];
    const PhaseIcon = phase.icon;
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              className={cn(
                "gap-1 text-xs cursor-default",
                phase.bgColor,
                `text-${phase.color}-700`,
                phase.isAccelerated && "animate-pulse"
              )}
            >
              <PhaseIcon className="w-3 h-3" />
              {phase.shortLabel}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-semibold">{phase.label}</p>
              <p className="text-xs text-slate-400">{phase.farReference}</p>
              <p className="text-xs">{phase.description}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="w-full" data-testid="phase-tracker">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">FAR Lifecycle Phase</span>
          </div>
          {isCombinedSynopsis && (
            <Badge className="bg-red-100 text-red-700 animate-pulse gap-1">
              <Zap className="w-3 h-3" />
              Accelerated Timeline
            </Badge>
          )}
        </div>
        
        {/* Phase Progress Bar */}
        <div className="relative">
          {/* Connection Line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200" />
          <div 
            className="absolute top-5 left-0 h-0.5 bg-teal-500 transition-all duration-500"
            style={{ 
              width: isCombinedSynopsis 
                ? '100%' 
                : `${(currentPhaseIndex / (PROCUREMENT_PHASES.length - 1)) * 100}%` 
            }}
          />
          
          {/* Phase Nodes */}
          <div className="relative flex justify-between">
            {PROCUREMENT_PHASES.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = phase.id === effectivePhase;
              const isCompleted = !isCombinedSynopsis && index < currentPhaseIndex;
              const isFuture = !isCombinedSynopsis && index > currentPhaseIndex;
              const isSkipped = isCombinedSynopsis && index > 0 && index < PROCUREMENT_PHASES.length - 1;
              
              return (
                <Tooltip key={phase.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handlePhaseClick(phase, index)}
                      disabled={!canEdit || isUpdating}
                      className={cn(
                        "relative flex flex-col items-center gap-1 group transition-all",
                        canEdit && !isUpdating && "cursor-pointer",
                        !canEdit && "cursor-default"
                      )}
                      data-testid={`phase-${phase.id}`}
                    >
                      {/* Phase Circle */}
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all z-10 bg-white",
                        isActive && `${phase.activeColor} border-transparent text-white shadow-lg scale-110`,
                        isCompleted && "bg-teal-500 border-teal-500 text-white",
                        isFuture && "bg-white border-slate-300 text-slate-400",
                        isSkipped && "bg-slate-100 border-dashed border-slate-300 text-slate-300",
                        canEdit && !isUpdating && !isActive && "group-hover:border-teal-400 group-hover:scale-105"
                      )}>
                        {isCompleted ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <PhaseIcon className={cn("w-5 h-5", isActive && phase.isAccelerated && "animate-pulse")} />
                        )}
                      </div>
                      
                      {/* Phase Label */}
                      <span className={cn(
                        "text-xs font-medium text-center max-w-[70px] leading-tight",
                        isActive && `text-${phase.color}-700`,
                        isCompleted && "text-teal-600",
                        isFuture && "text-slate-400",
                        isSkipped && "text-slate-300 line-through"
                      )}>
                        {phase.shortLabel}
                      </span>
                      
                      {/* Active Indicator */}
                      {isActive && (
                        <div className={cn(
                          "absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full",
                          phase.activeColor
                        )} />
                      )}
                    </button>
                  </TooltipTrigger>
                  
                  <TooltipContent 
                    side="bottom" 
                    className="max-w-sm p-4"
                    data-testid={`phase-tooltip-${phase.id}`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-900">{phase.label}</span>
                        <Badge variant="outline" className="text-xs gap-1">
                          <Info className="w-3 h-3" />
                          {phase.farReference}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{phase.description}</p>
                      <div className={cn(
                        "p-2 rounded-md text-xs",
                        phase.bgColor,
                        `text-${phase.color}-800`
                      )}>
                        <strong>Guidance:</strong> {phase.guidance}
                      </div>
                      {canEdit && !isActive && (
                        <p className="text-xs text-slate-400 italic">
                          Click to {index > currentPhaseIndex ? 'promote' : 'move back'} to this phase
                        </p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
        
        {/* Current Phase Details */}
        {(() => {
          const phase = PROCUREMENT_PHASES.find(p => p.id === effectivePhase) || PROCUREMENT_PHASES[0];
          return (
            <div className={cn(
              "mt-4 p-3 rounded-lg border",
              phase.bgColor,
              phase.borderColor
            )}>
              <div className="flex items-start gap-3">
                <div className={cn(
                  "p-2 rounded-lg",
                  phase.activeColor,
                  "text-white"
                )}>
                  <phase.icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn("font-semibold", `text-${phase.color}-800`)}>
                      {phase.label}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {phase.farReference}
                    </Badge>
                  </div>
                  <p className={cn("text-sm mt-1", `text-${phase.color}-700`)}>
                    {phase.guidance}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Phase Change Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="sm:max-w-md" data-testid="phase-change-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {targetPhase?.isPromotion ? (
                  <ArrowUp className="w-5 h-5 text-teal-600" />
                ) : (
                  <ArrowDown className="w-5 h-5 text-amber-600" />
                )}
                {targetPhase?.isPromotion ? 'Promote' : 'Move Back'} Pursuit Phase
              </DialogTitle>
              <DialogDescription>
                {targetPhase?.isPromotion 
                  ? 'Advancing this pursuit to a later procurement phase.'
                  : 'Moving this pursuit back to an earlier procurement phase.'
                }
              </DialogDescription>
            </DialogHeader>
            
            {targetPhase && (
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Badge className={cn(
                      "gap-1",
                      PROCUREMENT_PHASES[currentPhaseIndex]?.bgColor,
                      `text-${PROCUREMENT_PHASES[currentPhaseIndex]?.color}-700`
                    )}>
                      {PROCUREMENT_PHASES[currentPhaseIndex]?.shortLabel || 'Forecast'}
                    </Badge>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                  <div className="flex items-center gap-2">
                    <Badge className={cn(
                      "gap-1",
                      targetPhase.phase.bgColor,
                      `text-${targetPhase.phase.color}-700`
                    )}>
                      {targetPhase.phase.shortLabel}
                    </Badge>
                  </div>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg border",
                  targetPhase.phase.bgColor,
                  targetPhase.phase.borderColor
                )}>
                  <p className="text-sm font-medium mb-1">{targetPhase.phase.label}</p>
                  <p className="text-xs text-slate-600">{targetPhase.phase.description}</p>
                  <p className="text-xs text-slate-500 mt-2">
                    <strong>FAR Reference:</strong> {targetPhase.phase.farReference}
                  </p>
                </div>
                
                {targetPhase.phase.isAccelerated && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-red-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Accelerated Timeline</p>
                        <p className="text-xs text-red-600">
                          Combined Synopsis bypasses intermediate phases. 
                          Ensure your team is ready for rapid proposal development.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmDialog(false)}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmPhaseChange}
                disabled={isUpdating}
                className={cn(
                  targetPhase?.isPromotion 
                    ? "bg-teal-600 hover:bg-teal-700" 
                    : "bg-amber-600 hover:bg-amber-700"
                )}
                data-testid="confirm-phase-change"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    {targetPhase?.isPromotion ? (
                      <ArrowUp className="w-4 h-4 mr-2" />
                    ) : (
                      <ArrowDown className="w-4 h-4 mr-2" />
                    )}
                    Confirm {targetPhase?.isPromotion ? 'Promotion' : 'Change'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

// Export phases for use elsewhere (e.g., Sentinel integration)
export { PROCUREMENT_PHASES };
